import React, { useState, useEffect } from 'react';
import { adminApi } from '../../lib/api';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Plus,
  UserCheck,
  UserX,
  Trash2,
  Mail,
  Shield,
  User,
  Eye,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const AdminContractors = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToEditRate, setUserToEditRate] = useState(null);
  const [newHourlyRate, setNewHourlyRate] = useState('');
  const { startImpersonating } = useAuth();
  const navigate = useNavigate();
  
  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('contractor');
  const [contractorType, setContractorType] = useState('hourly');
  const [hourlyRate, setHourlyRate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await adminApi.getUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const userData = { name, email, password, role, contractor_type: contractorType };
      if (hourlyRate && role === 'contractor' && contractorType === 'hourly') {
        userData.hourly_rate = parseFloat(hourlyRate);
      }
      await adminApi.createUser(userData);
      toast.success('User created successfully');
      setCreateDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create user';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      await adminApi.toggleUserActive(userId);
      toast.success('User status updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleUpdateRate = async () => {
    if (!userToEditRate || !newHourlyRate) return;
    
    try {
      await adminApi.updateHourlyRate(userToEditRate.id, parseFloat(newHourlyRate));
      toast.success('Hourly rate updated');
      setRateDialogOpen(false);
      setUserToEditRate(null);
      setNewHourlyRate('');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update hourly rate');
    }
  };

  const openRateDialog = (user) => {
    setUserToEditRate(user);
    setNewHourlyRate(user.hourly_rate?.toString() || '');
    setRateDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await adminApi.deleteUser(userToDelete.id);
      toast.success('User deleted');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete user';
      toast.error(message);
    }
  };

  const resetForm = () => {
    setName('');
    setEmail('');
    setPassword('');
    setRole('contractor');
    setContractorType('hourly');
    setHourlyRate('');
  };

  const handleViewAs = (targetUser) => {
    startImpersonating(targetUser);
    if (targetUser.contractor_type === 'sales') {
      navigate('/sales');
    } else {
      navigate('/dashboard');
    }
    toast.success(`Viewing as ${targetUser.name}`);
  };

  const contractors = users.filter(u => u.role === 'contractor');
  const admins = users.filter(u => u.role === 'admin');

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-500">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-contractors-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
              Team
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Manage contractor and admin accounts
            </p>
          </div>

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brick-red hover:bg-[#c4151d] font-medium" data-testid="add-user-btn">
                <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Create New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Full Name</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Doe"
                    required
                    data-testid="create-name-input"
                    className="border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john@example.com"
                    required
                    data-testid="create-email-input"
                    className="border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    data-testid="create-password-input"
                    className="border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger data-testid="create-role-select" className="border-slate-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contractor">Contractor</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {role === 'contractor' && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Contractor Type</Label>
                      <Select value={contractorType} onValueChange={setContractorType}>
                        <SelectTrigger data-testid="create-contractor-type-select" className="border-slate-300">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hourly">Hourly (Time Tracking)</SelectItem>
                          <SelectItem value="sales">Sales (Commission Tracking)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {contractorType === 'hourly' && (
                      <div className="space-y-2">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Hourly Rate ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={hourlyRate}
                          onChange={(e) => setHourlyRate(e.target.value)}
                          placeholder="0.00"
                          data-testid="create-hourly-rate-input"
                          className="border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                        />
                      </div>
                    )}
                  </>
                )}
                <Button
                  type="submit"
                  className="w-full bg-brick-red hover:bg-[#c4151d] font-medium"
                  disabled={submitting}
                  data-testid="create-user-submit"
                >
                  {submitting ? 'Creating...' : 'Create User'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Contractors</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="contractors-count">{contractors.length}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-brick-cyan/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-brick-cyan" aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Admins</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="admins-count">{admins.length}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-brick-red/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-brick-red" aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users List */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-200">
            <CardTitle className="font-heading text-lg">All Users</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-200">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`user-row-${user.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${user.is_active ? (user.role === 'admin' ? 'bg-brick-red' : 'bg-brick-cyan') : 'bg-slate-300'}`}>
                      {user.role === 'admin' ? (
                        <Shield className="w-5 h-5 text-white" aria-hidden="true" />
                      ) : (
                        <User className="w-5 h-5 text-white" aria-hidden="true" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{user.name}</p>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Mail className="w-3 h-3" aria-hidden="true" />
                        <span className="font-mono text-xs">{user.email}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {user.role === 'contractor' && user.contractor_type === 'hourly' && (
                      <button 
                        className="flex items-center gap-1 cursor-pointer hover:bg-slate-100 px-2 py-1 rounded-md transition-colors"
                        onClick={() => openRateDialog(user)}
                        title="Click to edit hourly rate"
                        data-testid={`hourly-rate-${user.id}`}
                      >
                        <DollarSign className="w-4 h-4 text-green-600" aria-hidden="true" />
                        <span className="font-mono text-sm font-medium text-slate-700">
                          {user.hourly_rate ? `$${user.hourly_rate.toFixed(2)}/hr` : 'Set rate'}
                        </span>
                      </button>
                    )}
                    {user.role === 'contractor' && user.contractor_type === 'sales' && (
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 text-xs">
                        Sales
                      </Badge>
                    )}
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${user.role === 'admin' ? 'border-brick-red text-brick-red' : 'border-brick-cyan text-brick-cyan'}`}
                    >
                      {user.role}
                    </Badge>
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${user.is_active ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'}`}
                    >
                      {user.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <div className="flex gap-1">
                      {user.role === 'contractor' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewAs(user)}
                          title="View as this user"
                          data-testid={`view-as-${user.id}`}
                          className="hover:bg-brick-cyan/10 hover:text-brick-cyan"
                        >
                          <Eye className="w-4 h-4" aria-hidden="true" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(user.id)}
                        title={user.is_active ? 'Deactivate' : 'Activate'}
                        data-testid={`toggle-active-${user.id}`}
                        className="hover:bg-slate-100"
                      >
                        {user.is_active ? (
                          <UserX className="w-4 h-4 text-amber-600" aria-hidden="true" />
                        ) : (
                          <UserCheck className="w-4 h-4 text-green-600" aria-hidden="true" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setUserToDelete(user);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-user-${user.id}`}
                        className="hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" aria-hidden="true" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Delete User</DialogTitle>
            </DialogHeader>
            <p className="text-slate-500">
              Are you sure you want to delete <strong className="text-slate-900">{userToDelete?.name}</strong>? 
              This will also delete all their time entries. This action cannot be undone.
            </p>
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} data-testid="cancel-delete-btn">
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteUser}
                data-testid="confirm-delete-user"
              >
                Delete User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Hourly Rate Dialog */}
        <Dialog open={rateDialogOpen} onOpenChange={setRateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Update Hourly Rate</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-slate-500">
                Set the hourly rate for <strong className="text-slate-900">{userToEditRate?.name}</strong>
              </p>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Hourly Rate ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newHourlyRate}
                  onChange={(e) => setNewHourlyRate(e.target.value)}
                  placeholder="0.00"
                  data-testid="edit-hourly-rate-input"
                  className="border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setRateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateRate}
                className="bg-brick-red hover:bg-[#c4151d]"
                disabled={!newHourlyRate}
                data-testid="confirm-update-rate"
              >
                Update Rate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminContractors;
